import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const VisitorApprovalVM = () => {
  const { handleToastOpen } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [visitorData, setVisitorData] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false); // New loader state for actions
  const [disabled, setDisabled] = useState(false);
  const [openPhotoPreview, setOpenPhotoPreview] = useState(false);
  const [openDocumentPreview, setOpenDocumentPreview] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [showActionMessage, setShowActionMessage] = useState(false);

  // Computed properties
  const isFinalStatus =
    status && ["Approved", "Rejected", "On Hold"].includes(status);
  const showButtons = status && !["Approved", "Rejected"].includes(status);

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole !== "Approver") {
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      navigate("/visitor-mgmt/login");
      return;
    }

    fetchVisitorData();
  }, [id]);

  const fetchVisitorData = async () => {
    setLoading(true); // Set loading to true when fetching data
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/visitor/${id}`
      );

      // Handle the updated API response structure
      if (response.data.success && response.data.data) {
        setVisitorData(response.data.data);
        setStatus(response.data.data.status);
      } else {
        // If the API still returns data directly without the success wrapper
        setVisitorData(response.data);
        setStatus(response.data.status);
      }

      console.log("Visitor data:", response.data); // Debug log
    } catch (error) {
      console.error("Error fetching visitor data:", error);
      handleToastOpen("error", "Failed to fetch visitor data.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPhotoPreview = () => {
    if (visitorData && visitorData.photo_url) {
      setPhotoPreview(visitorData.photo_url);
      setOpenPhotoPreview(true);
    } else {
      handleToastOpen("error", "Photo not available");
    }
  };

  const handleClosePhotoPreview = () => {
    setOpenPhotoPreview(false);
    setPhotoPreview(null);
  };

  const handleOpenDocumentPreview = () => {
    if (visitorData && visitorData.document_url) {
      setDocumentPreview(visitorData.document_url);
      setOpenDocumentPreview(true);
    } else {
      handleToastOpen("error", "Document not available");
    }
  };

  const handleCloseDocumentPreview = () => {
    setOpenDocumentPreview(false);
    setDocumentPreview(null);
  };

  const updateStatus = async (newStatus) => {
    setDisabled(true);
    setActionLoading(true); // Set action loading to true
    try {
      const fe_url = window.location.origin;
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/updateVisitorStatusAndProcess/${id}?status=${newStatus}&fe_url=${fe_url}`
      );

      console.log("Status update response:", response.data); // Debug log

      setStatus(newStatus);
      setShowActionMessage(true);

      // Display appropriate success message based on the new status
      if (newStatus === "Approved") {
        handleToastOpen(
          "success",
          "Visitor request has been approved. A visitor pass has been sent to the visitor."
        );
      } else if (newStatus === "Rejected") {
        handleToastOpen(
          "success",
          "Visitor request has been rejected. The visitor has been notified."
        );
      } else if (newStatus === "On Hold") {
        handleToastOpen(
          "success",
          "Visitor request is now on hold. The visitor has been notified."
        );
      }
    } catch (error) {
      console.error(`Error updating status to ${newStatus}:`, error);
      handleToastOpen("error", `Error updating status to ${newStatus}.`);
    } finally {
      setDisabled(false);
      setActionLoading(false); // Set action loading to false
    }
  };

  const handleApprove = () => updateStatus("Approved");
  const handleReject = () => updateStatus("Rejected");
  const handleOnHold = () => updateStatus("On Hold");

  const handleHomeClick = () => {
    navigate("/visitor-mgmt/approver-dashboard");
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";

    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return the original string if formatting fails
    }
  };

  return {
    visitorData,
    status,
    loading,
    actionLoading, // Export the new action loading state
    disabled,
    openPhotoPreview,
    openDocumentPreview,
    photoPreview,
    documentPreview,
    showActionMessage,
    isFinalStatus,
    showButtons,
    handleHomeClick,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleApprove,
    handleReject,
    handleOnHold,
    formatDate,
  };
};

export default VisitorApprovalVM;